import Alpine from 'alpinejs'

const API = '/.netlify/functions'

Alpine.data('rsvp', () => ({
  step: 'search',
  searchName: '',
  searching: false,
  submitting: false,
  error: '',
  parties: [],
  party: null,
  editable: true,
  attending: null,
  guests: [],
  plusOneName: '',
  driving: null,
  email: '',
  message: '',
  toast: '',
  toastType: 'success',

  get displayName() {
    if (!this.party) return ''
    return this.party.displayName || this.party.names.join(', ')
  },

  get nameParts() {
    const name = this.displayName
    if (!name) return null

    let parts = null
    if (name.includes('&')) {
      parts = name.split('&').map((p) => p.trim())
    } else if (/ and /i.test(name) && !/\band\s+mrs\.?\b/i.test(name)) {
      parts = name.split(/ and /i).map((p) => p.trim())
    }

    if (parts && parts.length === 2) return parts
    return null
  },

  get nameClass() {
    if (this.nameParts) return 'text-3xl sm:text-4xl md:text-5xl'
    const len = this.displayName.length
    if (len > 35) return 'text-2xl sm:text-3xl md:text-4xl'
    if (len > 25) return 'text-3xl sm:text-4xl md:text-5xl'
    return 'text-4xl md:text-5xl'
  },

  get rsvp() {
    return this.party?.rsvp
  },

  get checkedCount() {
    return this.guests.filter((g) => g.checked).length
  },

  get lockedGuests() {
    const r = this.rsvp
    if (!r || r.attending !== 'yes') return ''
    const attending = r.guestsAttending ? r.guestsAttending.split(',').map((n) => n.trim()) : []
    if (r.plusOneName) attending.push(r.plusOneName)
    if (attending.length === 0) return '\u2014'
    if (attending.length === 1) return attending[0]
    return attending.slice(0, -1).join(', ') + ' & ' + attending[attending.length - 1]
  },

  get acceptClass() {
    const base = 'cursor-pointer py-4 rounded-lg text-lg font-semibold border-2 transition-all'
    if (this.attending === 'yes') return `${base} border-emerald-600 bg-emerald-600 text-white shadow-md`
    if (this.attending === 'no') return `${base} border-emerald-300 text-emerald-400 bg-white hover:border-emerald-400 hover:shadow-md`
    return `${base} border-emerald-400 text-emerald-600 bg-white hover:border-emerald-500 hover:shadow-md`
  },

  get declineClass() {
    const base = 'cursor-pointer py-4 rounded-lg text-lg font-semibold border-2 transition-all'
    if (this.attending === 'no') return `${base} border-gray-500 bg-gray-500 text-white shadow-md`
    if (this.attending === 'yes') return `${base} border-gray-300 text-gray-400 bg-white hover:border-gray-400 hover:shadow-md`
    return `${base} border-gray-300 text-gray-500 bg-white hover:border-gray-400 hover:shadow-md`
  },

  init() {
    const savedId = localStorage.getItem('rsvp_id')
    const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '')
    const id = savedId || hashParams.get('id')
    if (id) {
      this.lookupById(id)
    }

    window.addEventListener('hashchange', () => {
      const hash = window.location.hash
      if (hash === '#rsvp' || hash === '') {
        const still = localStorage.getItem('rsvp_id')
        if (!still && this.step !== 'search') {
          this.step = 'search'
          this.party = null
          this.error = ''
          this.$nextTick(() => this.$refs.nameInput?.focus())
        }
      }
    })
  },

  updateUrl(id) {
    const base = window.location.pathname
    if (id) {
      history.replaceState(null, '', `${base}#rsvp?id=${encodeURIComponent(id)}`)
    } else {
      history.replaceState(null, '', base)
    }
  },

  async lookupById(id) {
    try {
      const res = await fetch(`${API}/lookup?id=${encodeURIComponent(id)}`)
      if (!res.ok) return
      const data = await res.json()
      this.editable = data.editable
      if (data.parties.length === 1) {
        this.selectParty(data.parties[0], { scroll: false })
      }
    } catch {}
  },

  showToast(message, type = 'success', duration = 3000) {
    this.toast = message
    this.toastType = type
    if (this._toastTimer) clearTimeout(this._toastTimer)
    this._toastTimer = setTimeout(() => { this.toast = '' }, duration)
  },

  scrollToSection() {
    this.$nextTick(() => {
      document.getElementById('rsvp')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  },

  resetToSearch() {
    this.step = 'search'
    this.searchName = ''
    this.error = ''
    this.attending = null
    this.party = null
    this.parties = []
    localStorage.removeItem('rsvp_id')
    this.updateUrl(null)
    this.scrollToSection()
    this.$nextTick(() => this.$refs.nameInput?.focus())
  },

  selectParty(party, { scroll = true } = {}) {
    this.party = party
    this.error = ''
    this.attending = null
    this.updateUrl(party.id)

    const rsvp = party.rsvp
    const attendingNames = rsvp ? (rsvp.guestsAttending || '').split(',').map((n) => n.trim()).filter(Boolean) : []

    this.guests = party.names.map((name) => ({
      name,
      checked: rsvp ? attendingNames.includes(name) : true
    }))

    this.plusOneName = rsvp?.plusOneName || ''
    this.driving = rsvp?.driving ?? null
    this.email = rsvp?.email || ''
    this.message = rsvp?.message || ''

    if (rsvp) {
      this.attending = rsvp.attending
      this.step = 'confirmed'
    } else {
      this.step = 'form'
    }
    if (scroll) this.scrollToSection()
  },

  editRsvp() {
    this.step = 'form'
    this.scrollToSection()
  },

  toggleGuest(index) {
    if (!this.guests[index].checked && this.checkedCount < 1) {
      this.guests[index].checked = true
    }
  },

  async search() {
    const name = this.searchName.trim()
    if (!name) return

    this.error = ''
    this.searching = true

    try {
      const res = await fetch(`${API}/lookup?name=${encodeURIComponent(name)}`)
      const text = await res.text()
      let data
      try {
        data = JSON.parse(text)
      } catch {
        this.error = 'Something went wrong. Please try again or contact Hayley or Jarrett directly.'
        return
      }

      if (!res.ok) {
        this.error = data.error
        return
      }

      this.editable = data.editable

      if (data.parties.length === 1) {
        this.selectParty(data.parties[0])
      } else {
        this.parties = data.parties
        this.step = 'picker'
      }
    } catch {
      this.error = 'Something went wrong. Please try again or contact Hayley or Jarrett directly.'
    } finally {
      this.searching = false
    }
  },

  async submit() {
    if (!this.email.trim() && !this.rsvp?.email) {
      this.$refs.emailInput?.focus()
      this.showToast('Please enter your email address.', 'error', 4000)
      return
    }

    const selected = this.guests.filter((g) => g.checked).map((g) => g.name)
    const plusOne = this.plusOneName.trim()

    const payload = {
      partyId: this.party.id,
      partyName: this.displayName,
      attending: this.attending,
      guestsAttending: this.attending === 'yes' ? selected.join(', ') : '',
      plusOneName: this.attending === 'yes' ? plusOne : '',
      guestCount: this.attending === 'yes' ? selected.length + (plusOne ? 1 : 0) : 0,
      driving: this.attending === 'yes' ? this.driving : null,
      email: this.email.trim() || this.rsvp?.email || '',
      message: this.message.trim()
    }

    this.submitting = true

    try {
      const res = await fetch(`${API}/rsvp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })

      if (!res.ok) {
        const data = await res.json()
        this.showToast(data.error || 'Something went wrong. Please try again.', 'error', 4000)
        return
      }

      // Update party rsvp data in-place so confirmed view reflects changes
      this.party.rsvp = { ...payload, submittedAt: new Date().toISOString() }
      localStorage.setItem('rsvp_id', this.party.id)

      this.step = 'confirmed'
      this.scrollToSection()
      this.showToast(
        this.attending === 'yes'
          ? 'We can\'t wait to celebrate with you!'
          : 'Thanks for letting us know!'
      )
    } catch {
      this.showToast('Something went wrong. Please try again.', 'error', 4000)
    } finally {
      this.submitting = false
    }
  }
}))
